import type { FormEventHandler, KeyboardEventHandler, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import { cx } from '@/utils/cx';

const MIN_HEIGHT_PX = 120;
const MAX_VIEWPORT_FRACTION = 0.8;
const RESIZE_LABEL = 'Resize email body height';

interface ResizableEmailBodyProps {
  maxBodyHeight: string;
  embedded?: boolean;
  children: ReactNode;
  onKeyDownCapture?: KeyboardEventHandler<HTMLDivElement>;
  onInputCapture?: FormEventHandler<HTMLDivElement>;
}

/**
 * Drag-resizable wrapper around a `maxBodyHeight`-capped email body. Children
 * are passed straight through so the Plate subtree is never re-rendered as
 * height state changes during a drag.
 *
 * The outer wrapper owns the sizing (height / max-height) and hosts the
 * handle; an inner box owns the scrolling. Keeping the handle out of the
 * scroll container is what pins it to the visible bottom edge.
 */
export function ResizableEmailBody({
  maxBodyHeight,
  embedded,
  children,
  onKeyDownCapture,
  onInputCapture,
}: ResizableEmailBodyProps) {
  const defaultCapPx = parseInt(maxBodyHeight, 10) || 300;

  const [height, setHeight] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 768
  );
  const [isCoarsePointer, setIsCoarsePointer] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(hover: none) and (pointer: coarse)').matches
      : false
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  const maxPx = useMemo(
    () =>
      Math.max(
        Math.floor(viewportHeight * MAX_VIEWPORT_FRACTION),
        MIN_HEIGHT_PX
      ),
    [viewportHeight]
  );

  const clamp = useCallback(
    (px: number) => Math.min(Math.max(px, MIN_HEIGHT_PX), maxPx),
    [maxPx]
  );

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const base = wrapperRef.current?.offsetHeight ?? height ?? defaultCapPx;
      dragStartYRef.current = e.clientY;
      dragStartHeightRef.current = base;
      setIsResizing(true);
      document.body.classList.add('row-resize-active');
    },
    [height, defaultCapPx]
  );

  useEffect(() => {
    if (!isResizing) {
      return undefined;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const next = clamp(
        dragStartHeightRef.current + (e.clientY - dragStartYRef.current)
      );
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(() => setHeight(next));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.classList.remove('row-resize-active');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      document.body.classList.remove('row-resize-active');
    };
  }, [isResizing, clamp]);

  const handleKeyboardResize = useCallback(
    (delta: number) => {
      setHeight(prev =>
        clamp(
          (prev ?? wrapperRef.current?.offsetHeight ?? defaultCapPx) + delta
        )
      );
    },
    [clamp, defaultCapPx]
  );

  const handleReset = useCallback(() => {
    setHeight(null);
  }, []);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia('(hover: none) and (pointer: coarse)');
    setIsCoarsePointer(mql.matches);
    const handleChange = () => setIsCoarsePointer(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isResized = height !== null && !isCoarsePointer;

  return (
    <div
      ref={wrapperRef}
      className={cx(
        'relative flex flex-col',
        isResized && 'max-h-[80vh]',
        embedded && (isResized ? 'min-h-[120px]' : 'flex-1 min-h-[120px]')
      )}
      onKeyDownCapture={onKeyDownCapture}
      onInputCapture={onInputCapture}
      style={
        isResized ? { height: `${height}px` } : { maxHeight: maxBodyHeight }
      }
    >
      {/* The scrolling box is a child so the handle below stays pinned to
          the wrapper's bottom edge instead of scrolling away with the body
          content. `flex-1 min-h-0` makes it fill the wrapper exactly. */}
      <div className='min-h-0 flex-1 overflow-y-auto'>{children}</div>
      {!isCoarsePointer && (
        <EdgeResizeHandle
          edge='bottom'
          cornerGrip
          onResizeStart={handleResizeStart}
          onKeyboardResize={handleKeyboardResize}
          onDoubleClick={handleReset}
          onReset={handleReset}
          width={height ?? defaultCapPx}
          minWidth={MIN_HEIGHT_PX}
          maxWidth={maxPx}
          isResizing={isResizing}
          aria-label={RESIZE_LABEL}
        />
      )}
    </div>
  );
}
